import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { ArtiQAccepted } from "../../assets/icons/ArtiQAccepted";
import { ArtiQRejected } from "../../assets/icons/ArtiQRejected";
import { ArtiQUsableAccepted } from "../../assets/icons/ArtiQUsableAccepted";
import { styled } from "../../styles";
import { Tag } from "../Tag";

// ── Styled Components (pixel-matching SpirometrySection) ─────────────

const SpirometryContainer = styled("div", {
  height: "100%",
  width: "100%",
  padding: "24px 18px 18px 48px",
  borderLeft: "1px solid $gray-30",
  overflowY: "auto",

  "@media only screen and (max-width: 1250px)": {
    padding: "24px 2px 18px 10px",
  },
});

const HeaderContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  marginLeft: 0,
  marginBottom: "38px",
});

const Title = styled("h1", {
  fontWeight: 500,
  fontSize: "$text-xl",
  lineHeight: "22px",
  color: "$black",
});

const SubtitleGradeContainer = styled("div", {
  display: "flex",
  alignItems: "center",
  minHeight: "20px",
  marginTop: "4px",
  marginBottom: "12px",
  gap: "10px",
});

const Subtitle = styled("h2", {
  color: "$gray-80",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
  margin: 0,
});

const ContentContainer = styled("div", {
  display: "flex",
  gap: "64px",

  "@media only screen and (max-width: 1250px)": {
    gap: "2px",
  },
});

const ChartContainer = styled("div", {
  gap: "20px",
  display: "flex",
  flexDirection: "column",
  minWidth: "240px",
  width: "240px",
});

const ArtiQContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
});

const TableContainer = styled("div", {
  width: "100%",
  display: "flex",
  paddingRight: "60px",
  justifyContent: "flex-end",
});

const StyledTable = styled("table", {
  width: "198px",

  th: {
    lineHeight: "16px",
    fontWeight: 600,
    fontSize: "$text-md",
    color: "$gray-100",

    "&:nth-child(2)": {
      minWidth: "26px",
      paddingRight: "26px",
    },
    "&:last-child": {
      textAlign: "end",
      paddingRight: "7px",
      width: "50px",
    },
  },

  thead: {
    marginBottom: "8px",
  },

  td: {
    fontFamily: "$openSans",
    fontSize: "$text-md",
    marginBottom: "4px",
    color: "$gray-90",
    textAlign: "center",

    "&:first-child": {
      textAlign: "start",
    },
    "&:nth-child(2)": {
      minWidth: "26px",
      paddingRight: "24px",
    },
  },
});

const SpirometryListTitle = styled("div", {
  lineHeight: "16px",
  textAlign: "end",
  marginRight: "80px",
  marginBottom: "8px",
  fontWeight: 600,
  fontSize: "$text-md",
  color: "$gray-90",
  display: "flex",
  justifyContent: "flex-end",
});

const ListBlowItem = styled("div", {
  width: "220px",
  padding: "4px 16px",
  marginBottom: "2px",
  borderRadius: "8px",
  fontFamily: "$openSans",
  fontSize: "$text-md",
  color: "$gray-80",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",

  p: {
    paddingRight: "6px",
  },

  variants: {
    isHovered: {
      true: {
        background: "$gray-30",
      },
    },
    isOpaque: {
      true: {
        opacity: 0.28,
      },
    },
  },
});

const BlowColorDot = styled("div", {
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  flexShrink: 0,
});

const EmptyState = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$gray-80",
});

// ── Constants ────────────────────────────────────────────────────────

const BLOW_COLORS = [
  "#8974D4",
  "#69B6E1",
  "#DAA676",
  "#89D7AB",
  "#CE75C5",
  "#F5B0C0",
  "#FDDF94",
  "#939393",
];

const AXIS_TICK = {
  fill: "#727272",
  fontSize: "12px",
  fontWeight: 400,
};

// ── Mock Data ────────────────────────────────────────────────────────

type MockBlow = {
  time: string;
  fvc: number;
  fev1: number;
  fev1_fvc: number;
  pef: number;
  predictedFvc: number;
  predictedFev1: number;
  acceptability: "accepted" | "usable" | "rejected";
  flowVolume: { x: number; y: number }[];
  timeVolume: { x: number; y: number }[];
};

function generateFlowVolumeCurve(
  pef: number,
  fvc: number,
  seed: number
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  const steps = 50;
  const s = seed * 0.01;

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const vol = t * fvc;
    let flow: number;
    if (t < 0.1) {
      flow = (t / 0.1) * pef;
    } else {
      flow = pef * Math.exp(-2.5 * (t - 0.1) + s * 0.1) * (1 - t * 0.3);
    }
    points.push({
      x: Math.round(vol * 100) / 100,
      y: Math.round(Math.max(0, flow) * 100) / 100,
    });
  }
  return points;
}

function generateTimeVolumeCurve(
  fvc: number,
  seed: number
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];
  const steps = 50;
  const rate = 3.5 + seed * 0.05;

  for (let i = 0; i <= steps; i += 1) {
    const time = (i / steps) * 6;
    const vol = fvc * (1 - Math.exp(-rate * (time / 6)));
    points.push({
      x: Math.round(time * 100) / 100,
      y: Math.round(vol * 100) / 100,
    });
  }
  return points;
}

const MOCK_BLOWS: MockBlow[] = [
  {
    time: "8:50:40 AM",
    fvc: 3.42,
    fev1: 2.81,
    fev1_fvc: 0.82,
    pef: 7.2,
    predictedFvc: 4.1,
    predictedFev1: 3.4,
    acceptability: "accepted",
    flowVolume: generateFlowVolumeCurve(7.2, 3.42, 1),
    timeVolume: generateTimeVolumeCurve(3.42, 1),
  },
  {
    time: "8:51:22 AM",
    fvc: 3.38,
    fev1: 2.76,
    fev1_fvc: 0.82,
    pef: 6.9,
    predictedFvc: 4.1,
    predictedFev1: 3.4,
    acceptability: "accepted",
    flowVolume: generateFlowVolumeCurve(6.9, 3.38, 2),
    timeVolume: generateTimeVolumeCurve(3.38, 2),
  },
  {
    time: "8:52:05 AM",
    fvc: 3.15,
    fev1: 2.52,
    fev1_fvc: 0.8,
    pef: 6.1,
    predictedFvc: 4.1,
    predictedFev1: 3.4,
    acceptability: "usable",
    flowVolume: generateFlowVolumeCurve(6.1, 3.15, 3),
    timeVolume: generateTimeVolumeCurve(3.15, 3),
  },
];

// ── Sub-Components ───────────────────────────────────────────────────

function SpirometryMiniChart({
  data,
  xLabel,
  yLabel,
  xDomain,
  yDomain,
  selectedBlow,
  hoveredBlow,
  onMouseEnter,
  onMouseLeave,
}: {
  data: MockBlow[];
  xLabel: string;
  yLabel: string;
  xDomain: [number, number];
  yDomain: [number, number];
  selectedBlow: number;
  hoveredBlow: number | undefined;
  onMouseEnter: (i: number) => void;
  onMouseLeave: () => void;
}) {
  return (
    <ResponsiveContainer width="100%" height={207}>
      <LineChart margin={{ right: 20, top: 8 }}>
        <CartesianGrid strokeDasharray="2" vertical={false} />
        <XAxis
          dataKey="x"
          type="number"
          domain={xDomain}
          minTickGap={2}
          allowDecimals={false}
          tickMargin={14}
          stroke="#D8D8D8"
          height={48}
          tick={AXIS_TICK}
          tickFormatter={(v) => Number.parseFloat(v).toFixed(1)}
          label={{
            value: xLabel,
            fontSize: "12px",
            position: "insideBottomLeft",
          }}
        />
        <YAxis
          domain={yDomain}
          width={42}
          tickMargin={8}
          stroke="#D8D8D8"
          tick={AXIS_TICK}
          label={{
            value: yLabel,
            angle: -90,
            fontSize: "12px",
            position: "insideLeft",
          }}
        />
        {data.map((blow, i) => {
          const isActive = i === selectedBlow || i === hoveredBlow;
          const chartData =
            xLabel === "Vol (L)" ? blow.flowVolume : blow.timeVolume;
          return (
            <Line
              key={`blow-${blow.time}`}
              data={chartData}
              dataKey="y"
              stroke={BLOW_COLORS[i]}
              strokeWidth={isActive ? 2 : 1.4}
              strokeOpacity={isActive ? 1 : 0.28}
              dot={false}
              connectNulls
              isAnimationActive={false}
              cursor="pointer"
              onMouseEnter={() => onMouseEnter(i)}
              onMouseLeave={onMouseLeave}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
}

function SpirometryValuesTable({ blows }: { blows: MockBlow[] }) {
  const best = useMemo(() => {
    const maxFvc = Math.max(...blows.map((b) => b.fvc));
    const maxFev1 = Math.max(...blows.map((b) => b.fev1));
    const maxRatio = Math.max(...blows.map((b) => b.fev1_fvc));
    const maxPef = Math.max(...blows.map((b) => b.pef));
    const bestFvcBlow = blows.find((b) => b.fvc === maxFvc);
    const bestFev1Blow = blows.find((b) => b.fev1 === maxFev1);

    return {
      fvc: maxFvc,
      fev1: maxFev1,
      fev1_fvc: maxRatio,
      pef: maxPef,
      fvcPred: bestFvcBlow
        ? Math.round((maxFvc / bestFvcBlow.predictedFvc) * 100)
        : "-",
      fev1Pred: bestFev1Blow
        ? Math.round((maxFev1 / bestFev1Blow.predictedFev1) * 100)
        : "-",
    };
  }, [blows]);

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th aria-label="Measurement" />
            <th>Best</th>
            <th>% Pred</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>FVC (L)</td>
            <td>{best.fvc}</td>
            <td>{best.fvcPred}</td>
          </tr>
          <tr>
            <td>FEV1 (L)</td>
            <td>{best.fev1}</td>
            <td>{best.fev1Pred}</td>
          </tr>
          <tr>
            <td>FEV1/FVC (%)</td>
            <td>{Math.round(best.fev1_fvc * 100)}%</td>
            <td aria-label="No predicted value" />
          </tr>
          <tr>
            <td>PEF (L/s)</td>
            <td>{best.pef}</td>
            <td aria-label="No predicted value" />
          </tr>
        </tbody>
      </StyledTable>
    </TableContainer>
  );
}

function AcceptIcon({ status }: { status: string }) {
  if (status === "accepted") return <ArtiQAccepted />;
  if (status === "usable") return <ArtiQUsableAccepted />;
  return <ArtiQRejected />;
}

function BlowAcceptabilityList({
  blows,
  selectedBlow,
  hoveredBlow,
  onHover,
  onSelect,
}: {
  blows: MockBlow[];
  selectedBlow: number;
  hoveredBlow: number | undefined;
  onHover: (i: number | undefined) => void;
  onSelect: (i: number) => void;
}) {
  return (
    <div>
      <SpirometryListTitle>Accept.</SpirometryListTitle>
      {blows.map((blow, i) => {
        const isActive = i === selectedBlow || i === hoveredBlow;
        return (
          <ListBlowItem
            key={blow.time}
            isHovered={isActive}
            onMouseEnter={() => onHover(i)}
            onMouseLeave={() => onHover(undefined)}
            onClick={() => onSelect(i)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <p>{blow.time}</p>
              <BlowColorDot style={{ backgroundColor: BLOW_COLORS[i] }} />
            </div>
            <AcceptIcon status={blow.acceptability} />
          </ListBlowItem>
        );
      })}
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────

type SpirometryDetailsPocProps = {
  selectedPointTime?: string | null;
};

export function SpirometryDetailsPoc({
  selectedPointTime,
}: SpirometryDetailsPocProps) {
  const [selectedBlow, setSelectedBlow] = useState(0);
  const [hoveredBlow, setHoveredBlow] = useState<number | undefined>();

  if (!selectedPointTime) {
    return (
      <SpirometryContainer>
        <EmptyState>Select a data point to view session details</EmptyState>
      </SpirometryContainer>
    );
  }

  return (
    <SpirometryContainer>
      <HeaderContainer>
        <Title>Selected session</Title>
        <SubtitleGradeContainer>
          <Subtitle>{selectedPointTime}</Subtitle>
          <Tag variant="success">FVC = A</Tag>
          <Tag variant="success">FEV1 = A</Tag>
        </SubtitleGradeContainer>
      </HeaderContainer>

      <ContentContainer>
        <ChartContainer>
          <SpirometryMiniChart
            data={MOCK_BLOWS}
            xLabel="Vol (L)"
            yLabel="Flow (L/s)"
            xDomain={[0, 5]}
            yDomain={[0, 10]}
            selectedBlow={selectedBlow}
            hoveredBlow={hoveredBlow}
            onMouseEnter={setHoveredBlow}
            onMouseLeave={() => setHoveredBlow(undefined)}
          />
          <SpirometryMiniChart
            data={MOCK_BLOWS}
            xLabel="Time (s)"
            yLabel="Vol (L)"
            xDomain={[0, 6]}
            yDomain={[0, 5]}
            selectedBlow={selectedBlow}
            hoveredBlow={hoveredBlow}
            onMouseEnter={setHoveredBlow}
            onMouseLeave={() => setHoveredBlow(undefined)}
          />
        </ChartContainer>

        <ArtiQContainer>
          <SpirometryValuesTable blows={MOCK_BLOWS} />
          <BlowAcceptabilityList
            blows={MOCK_BLOWS}
            selectedBlow={selectedBlow}
            hoveredBlow={hoveredBlow}
            onHover={setHoveredBlow}
            onSelect={setSelectedBlow}
          />
        </ArtiQContainer>
      </ContentContainer>
    </SpirometryContainer>
  );
}
